import { bffInstance } from "../api/axiosInstance";
import { useQueryClient, useMutation } from "react-query";
import { useNotificationStore } from "./NotificationStore";
import { INotification } from "./NotificationInterface";

const updateUserReadMessage = async (messageId: string, read: boolean) => {
  const res = await bffInstance.put("/updateUserReadMessage", { messageId, read });
  return res.data;
};

const updateAllUserReadMessages = async (messageIds: string[]) => {
  const res = await bffInstance.put("/updateAllUserReadMessages", { messageIds });
  return res.data;
};

const deleteUserMessage = async (messageId: string) => {
  const res = await bffInstance.put("/deleteUserMessage", { messageId });
  return res.data;
};

const deleteAllUserMessages = async (messageIds: string[]) => {
  const res = await bffInstance.put("/deleteAllUserMessages", { messageIds });
  return res.data;
};

export function useUpdateNotification() {
  const queryClient = useQueryClient();
  const { read } = useNotificationStore();

  const { data, mutate } = useMutation(
    ({ notification, read: isRead = true }: { notification: INotification; read?: boolean }) =>
      updateUserReadMessage(notification.messageId!, isRead),
    {
      onMutate: async ({ notification, read: isRead = true }) => {
        read(notification, isRead);
      },
      onSuccess: () => {
        queryClient.invalidateQueries("notification");
      },
    }
  );
  return { data, mutate };
}

export function useMarkAllNotificationsRead() {
  const queryClient = useQueryClient();
  const { notifications, readAll } = useNotificationStore();

  const { data, mutate } = useMutation(
    () => {
      const unreadIds = notifications
        .filter((n: INotification) => !n.read && !n.deleted)
        .map((n: INotification) => n.messageId!);
      return updateAllUserReadMessages(unreadIds);
    },
    {
      onMutate: async () => {
        readAll();
      },
      onSuccess: () => {
        queryClient.invalidateQueries("notification");
      },
    }
  );
  return { data, mutate };
}

export function useDeleteNotification() {
  const queryClient = useQueryClient();
  const { remove } = useNotificationStore();

  const { data, mutate } = useMutation(
    (notification: INotification) => deleteUserMessage(notification.messageId!),
    {
      onMutate: async (notification: INotification) => {
        remove(notification);
      },
      onSuccess: () => {
        queryClient.invalidateQueries("notification");
      },
    }
  );
  return { data, mutate };
}

export function useDeleteAllNotification() {
  const queryClient = useQueryClient();
  const { removeAll } = useNotificationStore();

  const { data, mutate } = useMutation(
    (notifications: string[]) => deleteAllUserMessages(notifications),
    {
      onMutate: async (notifications: string[]) => {
        removeAll(notifications);
      },
      onSuccess: () => {
        queryClient.invalidateQueries("notification");
      },
    }
  );
  return { data, mutate };
}
